import React, { useCallback, useState } from 'react';

import { Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import UncreditedReservationsAlert from 'components/UncreditedReservationsAlert';
import useDeleteAthleteEvents from 'hooks/athleteEvent/useDeleteAthleteEvents';
import { MRT_PaginationState, MRT_RowSelectionState, MRT_SortingState } from 'mantine-react-table';
import { AthleteEvent, AthleteRetrieve } from 'types';
import notificationHandler from 'utils/notifications';

import AthleteReservationBulkDeleteModal from './components/AthleteReservationBulkDeleteModal';
import AthleteReservationsFilters from './components/AthleteReservationsFilters';
import AthleteReservationsTableContainer from './components/AthleteReservationsTableContainer';
import { SearchFilters } from './types';

interface IReservationsProps {
  athlete: AthleteRetrieve;
  permission?: boolean;
}

const Reservations = ({ athlete, permission }: IReservationsProps): JSX.Element => {
  const today = new Date();
  const initialSearchFiltersValue: SearchFilters = {
    date: [today, null],
    eventTypes: [],
    athlete: athlete.athleteId,
    isBulkAdd: null,
    weekdays: [],
  };

  const [searchFilters, setSearchFilters] = useState<SearchFilters>(initialSearchFiltersValue);

  const [tableData, setTableData] = useState<AthleteEvent[]>([]);
  const existsUncreditedReservations = tableData.some(ae => !ae.isCredited);

  const deleteBulkMutation = useDeleteAthleteEvents();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const { successNotification, errorNotification } = notificationHandler();
  const handleDeleteBulkAthleteEvents = useCallback((): void => {
    const athleteEvents = Object.keys(rowSelection);
    deleteBulkMutation.mutate(
      {
        data: {
          athleteEvents: athleteEvents.map(aeId => ({
            athleteEventId: aeId,
            type: tableData.filter(ae => ae.athleteEventId === aeId)[0].type,
          })),
        },
      },
      {
        onSuccess: () => {
          setRowSelection({});
          closeDeleteModal();
          successNotification('Attendance records delete success.');
        },
        onError: error => {
          errorNotification('Attendance records delete fail.', error.response?.data.message);
        },
      },
    );
  }, [
    deleteBulkMutation,
    errorNotification,
    rowSelection,
    successNotification,
    tableData,
    closeDeleteModal,
  ]);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'startDate', desc: true }]);

  return (
    <>
      <AthleteReservationsFilters
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        initialSearchFiltersValue={initialSearchFiltersValue}
        handleDeleteBulkAthleteEvents={openDeleteModal}
        rowSelection={rowSelection}
        pagination={pagination}
        sorting={sorting}
      />

      {existsUncreditedReservations && (
        <Box mb='1rem' mx='1rem'>
          <UncreditedReservationsAlert />
        </Box>
      )}

      <AthleteReservationsTableContainer
        tableData={tableData}
        setTableData={setTableData}
        searchFilters={searchFilters}
        permission={permission}
        setRowSelection={setRowSelection}
        rowSelection={rowSelection}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />
      <AthleteReservationBulkDeleteModal
        handleDelete={handleDeleteBulkAthleteEvents}
        opened={deleteModalOpened}
        deleteMutationLoading={deleteBulkMutation.isLoading}
        handleClose={closeDeleteModal}
      />
    </>
  );
};

export default Reservations;
